import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
export const SiteAddressSchema = z.object({
    street: z.string(),
    zipCode: z.string(),
    city: z.string(),
    country: z.string(),
    complement: z.string().nullable().optional(),
});
/**
 * Mirrors the sites listing response. `externalSiteId` is exposed here because
 * the sync flow round-trips it; removing vendor ids from these contracts is
 * tracked in TECH_DEBT.md (chantier 7).
 */
export const SiteSchema = z.object({
    id: z.string(),
    name: z.string(),
    address: SiteAddressSchema,
    externalSiteId: z.string(),
    organizationId: z.string(),
});
export const NotSyncedSiteSchema = z.object({
    externalSiteId: z.string(),
    name: z.string(),
    address: SiteAddressSchema,
});
/**
 * Sewan refuses a partial address, so `address` is either omitted (rename
 * only) or complete.
 */
export const UpdateSiteBodySchema = z.object({
    name: z.string().trim().min(1),
    address: z
        .object({
        street: z.string().trim().min(1),
        zipCode: z.string().trim().min(1),
        city: z.string().trim().min(1),
        country: z.string().trim().min(1),
        complement: z.string().trim().optional(),
    })
        .optional(),
});
export const SyncSitesBodySchema = z.object({
    sites: z
        .array(z.object({
        name: z.string().min(1),
        externalSiteId: z.string().min(1),
        address: z.object({
            street: z.string().min(1),
            zipCode: z.string().min(1),
            city: z.string().min(1),
            country: z.string().min(1),
            complement: z.string().optional(),
        }),
    }))
        .nonempty(),
});
export const siteContract = c.router({
    listSites: {
        method: 'GET',
        path: '/organization/:organizationId/sites',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        responses: {
            200: z.array(SiteSchema),
        },
        summary: 'List the billing centres of an organization',
    },
    listNotSyncedSites: {
        method: 'GET',
        path: '/organization/:organizationId/sites/not-synced',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        responses: {
            200: z.array(NotSyncedSiteSchema),
        },
        summary: 'List the provider sites not yet synced locally',
    },
    syncSites: {
        method: 'POST',
        path: '/organization/:organizationId/sites/sync',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        body: SyncSitesBodySchema,
        responses: {
            201: z.object({ success: z.boolean() }),
        },
        summary: 'Import provider sites as local billing centres',
    },
    updateSite: {
        method: 'PUT',
        path: '/sites/:siteId',
        pathParams: z.object({ siteId: z.string().min(1) }),
        body: UpdateSiteBodySchema,
        responses: {
            200: SiteSchema,
        },
        summary: 'Update a billing centre (name, address)',
    },
});
